import React, { useCallback, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation, useNavigate } from 'react-router-dom';
import { api } from '../../api/client';
import type { Live, Work } from '../../api/types';
import { PageHeader } from '../../components/PageHeader';
import { WorksPanel } from './panels/WorksPanel';
import { LivesPanel } from './panels/LivesPanel';
import { BioPanel } from './panels/BioPanel';
import { ContactsPanel } from './panels/ContactsPanel';
import './Admin.css';

type Tab = 'works' | 'lives' | 'bio' | 'contacts';

function tabFromPath(pathname: string): Tab | null {
    if (pathname.startsWith('/admin/lives')) return 'lives';
    if (pathname.startsWith('/admin/works')) return 'works';
    if (pathname.startsWith('/admin/bio')) return 'bio';
    if (pathname.startsWith('/admin/contacts')) return 'contacts';
    return null;
}

export default function Admin() {
    const [authState, setAuthState] = useState<'loading' | 'in' | 'out'>('loading');
    const location = useLocation();
    const navigate = useNavigate();
    const [tab, setTab] = useState<Tab>(() => tabFromPath(location.pathname) ?? 'works');

    // Keep the active tab in sync with the URL (e.g. browser back/forward,
    // direct hits to /admin/lives/123).
    useEffect(() => {
        const next = tabFromPath(location.pathname);
        if (next && next !== tab) setTab(next);
    }, [location.pathname, tab]);

    useEffect(() => {
        api.auth
            .me()
            .then((r) => setAuthState(r.authenticated ? 'in' : 'out'))
            .catch(() => setAuthState('out'));
    }, []);

    const handleLoggedIn = useCallback(() => setAuthState('in'), []);
    const handleLogout = useCallback(async () => {
        await api.auth.logout().catch(() => undefined);
        setAuthState('out');
    }, []);

    const selectTab = (next: Tab) => {
        // Clicking a tab clears any edit sub-route so the URL matches the view.
        setTab(next);
        if (location.pathname !== '/admin') navigate('/admin');
    };

    return (
        <div className="admin-page">
            <Helmet>
                <title>Admin · Lo.Sai</title>
                <meta name="robots" content="noindex" />
            </Helmet>
            <PageHeader title="ADMIN" />

            {authState === 'loading' && (
                <div className="admin-container"><p>Loading…</p></div>
            )}

            {authState === 'out' && <LoginForm onSuccess={handleLoggedIn} />}

            {authState === 'in' && (
                <div className="admin-container">
                    <div className="admin-tabs">
                        <button
                            className={`admin-tab ${tab === 'works' ? 'is-active' : ''}`}
                            onClick={() => selectTab('works')}
                        >Works</button>
                        <button
                            className={`admin-tab ${tab === 'lives' ? 'is-active' : ''}`}
                            onClick={() => selectTab('lives')}
                        >Lives</button>
                        <button
                            className={`admin-tab ${tab === 'bio' ? 'is-active' : ''}`}
                            onClick={() => selectTab('bio')}
                        >Bio</button>
                        <button
                            className={`admin-tab ${tab === 'contacts' ? 'is-active' : ''}`}
                            onClick={() => selectTab('contacts')}
                        >Contacts</button>
                        <button className="admin-logout" onClick={handleLogout}>Log out</button>
                    </div>

                    {tab === 'works' && <WorksPanel />}
                    {tab === 'lives' && <LivesPanel />}
                    {tab === 'bio' && <BioPanel />}
                    {tab === 'contacts' && <ContactsPanel />}
                </div>
            )}
        </div>
    );
}

function LoginForm({ onSuccess }: { onSuccess: () => void }) {
    const [password, setPassword] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const submit = async (e: React.FormEvent) => {
        e.preventDefault();
        setBusy(true);
        setError(null);
        try {
            await api.auth.login(password);
            onSuccess();
        } catch (err: any) {
            setError(err?.message ?? 'Login failed');
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="admin-container admin-login">
            <form onSubmit={submit} className="admin-login-form">
                <label>
                    <span>Password</span>
                    <input
                        type="password"
                        autoFocus
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                    />
                </label>
                {error && <div className="admin-error">{error}</div>}
                <button type="submit" disabled={busy || !password}>
                    {busy ? 'Signing in…' : 'Sign in'}
                </button>
            </form>
        </div>
    );
}

export type { Live, Work };
